import * as CollapsiblePrimitive from "@radix-ui/react-collapsible";
import {
  BookOpen,
  Check,
  ChevronRight,
  Circle,
  CircleAlert,
  Clock3,
  FileText,
  Loader2,
  Search,
  TerminalSquare,
  Wrench,
} from "lucide-react";
import {
  type ComponentType,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";

import { useMonkeysLocaleMessages } from "../../provider";
import { cn } from "../layout";
import {
  type AgentWorkbenchAppearanceOptions,
  useAgentWorkbenchAppearance,
} from "./appearance";

export type AgentWorkbenchProcessStatus =
  | "queued"
  | "running"
  | "waiting_approval"
  | "completed"
  | "failed"
  | "stopped";

export interface AgentWorkbenchProcessProps
  extends AgentWorkbenchAppearanceOptions {
  status: AgentWorkbenchProcessStatus;
  children: ReactNode;
  durationMs?: number;
  completedCount?: number;
  totalCount?: number;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
  contentClassName?: string;
  label?: string;
  showStatusIcon?: boolean;
}

function formatDuration(durationMs: number): string {
  const totalSeconds = Math.max(0, Math.floor(durationMs / 1_000));
  const hours = Math.floor(totalSeconds / 3_600);
  const minutes = Math.floor((totalSeconds % 3_600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  if (minutes === 0) return `${seconds}s`;
  return `${minutes}m ${seconds}s`;
}

function isActive(status: AgentWorkbenchProcessStatus): boolean {
  return (
    status === "queued" || status === "running" || status === "waiting_approval"
  );
}

export function AgentWorkbenchProcess({
  status,
  children,
  durationMs,
  completedCount,
  totalCount,
  open,
  defaultOpen,
  onOpenChange,
  className,
  contentClassName,
  label,
  showStatusIcon = true,
  variant,
  density,
}: AgentWorkbenchProcessProps) {
  const appearance = useAgentWorkbenchAppearance({ variant, density });
  const compact = appearance.density === "compact";
  const messages = useMonkeysLocaleMessages().agentWorkbench;
  const controlled = open !== undefined;
  const [internalOpen, setInternalOpen] = useState(
    defaultOpen ?? isActive(status),
  );
  const userChangedOpen = useRef(false);
  const previousStatus = useRef(status);
  const resolvedOpen = controlled ? open : internalOpen;

  useEffect(() => {
    if (
      controlled ||
      userChangedOpen.current ||
      previousStatus.current === status
    )
      return;
    previousStatus.current = status;
    setInternalOpen(isActive(status));
  }, [controlled, status]);

  const handleOpenChange = (nextOpen: boolean) => {
    userChangedOpen.current = true;
    if (!controlled) setInternalOpen(nextOpen);
    onOpenChange?.(nextOpen);
  };

  const duration =
    durationMs === undefined ? undefined : formatDuration(durationMs);
  const title =
    label ??
    (status === "queued"
      ? messages.process.queued
      : status === "waiting_approval"
        ? messages.process.waitingApproval
        : status === "running"
          ? messages.process.running
          : status === "failed"
            ? messages.process.failed
            : status === "stopped"
              ? messages.process.stopped
              : duration
                ? messages.process.processedFor({ duration })
                : messages.process.processed);
  const showCount = totalCount !== undefined && totalCount > 0;

  return (
    <CollapsiblePrimitive.Root
      open={resolvedOpen}
      onOpenChange={handleOpenChange}
      className={cn("group/process w-full min-w-0", className)}
      data-agent-process-status={status}
      data-agent-workbench-variant={appearance.variant}
      data-agent-workbench-density={appearance.density}
      aria-busy={isActive(status) ? "true" : undefined}
    >
      <CollapsiblePrimitive.Trigger
        className={cn(
          "flex w-full min-w-0 items-center gap-2 border-b border-border/70 text-left text-sm text-muted-foreground outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring",
          compact ? "min-h-8 py-1.5" : "min-h-9 py-2",
        )}
      >
        {showStatusIcon ? (
          status === "running" || status === "queued" ? (
            <Loader2
              className="size-4 shrink-0 animate-spin"
              aria-hidden="true"
            />
          ) : status === "waiting_approval" ? (
            <Clock3
              className="size-4 shrink-0 text-warning-foreground"
              aria-hidden="true"
            />
          ) : status === "failed" ? (
            <CircleAlert
              className="size-4 shrink-0 text-destructive-text"
              aria-hidden="true"
            />
          ) : status === "completed" ? (
            <Check
              className="size-4 shrink-0 text-success"
              aria-hidden="true"
            />
          ) : (
            <Clock3
              className="size-4 shrink-0 text-muted-foreground"
              aria-hidden="true"
            />
          )
        ) : null}
        <span className="min-w-0 flex-1 truncate">{title}</span>
        {showCount ? (
          <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs text-foreground">
            {messages.process.completedCount({
              completed: completedCount ?? 0,
              total: totalCount,
            })}
          </span>
        ) : null}
        <ChevronRight
          className="size-4 shrink-0 transition-transform group-data-[state=open]/process:rotate-90"
          aria-hidden="true"
        />
      </CollapsiblePrimitive.Trigger>
      <CollapsiblePrimitive.Content
        className={cn(
          "overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down",
          contentClassName,
        )}
      >
        <div
          className={cn(
            "flex min-w-0 flex-col",
            compact ? "gap-2 py-3" : "gap-3 py-4",
          )}
        >
          {children}
        </div>
      </CollapsiblePrimitive.Content>
    </CollapsiblePrimitive.Root>
  );
}

export type AgentWorkbenchActionKind =
  | "read"
  | "search"
  | "tool"
  | "command"
  | "file";

export type AgentWorkbenchActionStatus =
  | "pending"
  | "running"
  | "completed"
  | "failed"
  | "denied";

export interface AgentWorkbenchActionProps {
  summary: ReactNode;
  kind?: AgentWorkbenchActionKind;
  status?: AgentWorkbenchActionStatus;
  children?: ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  icon?: ComponentType<{
    className?: string;
    "aria-hidden"?: boolean | "true";
  }>;
  className?: string;
  contentClassName?: string;
}

const ACTION_ICONS: Record<
  AgentWorkbenchActionKind,
  ComponentType<{ className?: string }>
> = {
  read: BookOpen,
  search: Search,
  tool: Wrench,
  command: TerminalSquare,
  file: FileText,
};

function ActionStatusIcon({ status }: { status: AgentWorkbenchActionStatus }) {
  if (status === "running" || status === "pending") {
    return (
      <Loader2 className="size-3.5 shrink-0 animate-spin" aria-hidden="true" />
    );
  }
  if (status === "failed" || status === "denied") {
    return (
      <CircleAlert
        className="size-3.5 shrink-0 text-destructive-text"
        aria-hidden="true"
      />
    );
  }
  if (status === "completed") {
    return <Check className="size-3.5 shrink-0" aria-hidden="true" />;
  }
  return <Circle className="size-3.5 shrink-0" aria-hidden="true" />;
}

export function AgentWorkbenchAction({
  summary,
  kind = "tool",
  status = "completed",
  children,
  open,
  defaultOpen = false,
  onOpenChange,
  icon,
  className,
  contentClassName,
}: AgentWorkbenchActionProps) {
  const Icon = icon ?? ACTION_ICONS[kind];
  const row = (
    <>
      <Icon className="size-4 shrink-0" aria-hidden="true" />
      <span className="min-w-0 flex-1 truncate">{summary}</span>
      <ActionStatusIcon status={status} />
      {children ? (
        <ChevronRight
          className="size-3.5 shrink-0 transition-transform group-data-[state=open]/action:rotate-90"
          aria-hidden="true"
        />
      ) : null}
    </>
  );

  if (!children) {
    return (
      <div
        className={cn(
          "flex min-h-7 min-w-0 items-center gap-2 text-[13px] text-muted-foreground",
          className,
        )}
        data-agent-action-status={status}
      >
        {row}
      </div>
    );
  }

  return (
    <CollapsiblePrimitive.Root
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      className={cn("group/action min-w-0", className)}
      data-agent-action-status={status}
    >
      <CollapsiblePrimitive.Trigger className="flex min-h-7 w-full min-w-0 items-center gap-2 text-left text-[13px] text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring">
        {row}
      </CollapsiblePrimitive.Trigger>
      <CollapsiblePrimitive.Content className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
        <div
          className={cn(
            "ml-6 mt-2 min-w-0 border-l border-border pl-3",
            contentClassName,
          )}
        >
          {children}
        </div>
      </CollapsiblePrimitive.Content>
    </CollapsiblePrimitive.Root>
  );
}
